import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = "https://newalemny.3almny.app/api";

// Fetch teacher class files
export const fetchTeacherClassFiles = createAsyncThunk(
  "teacherFiles/fetchTeacherClassFiles",
  async ({ classId, page = 1 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${BASE_URL}/teacher_files/my_class_files/${classId}?page=${page}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return data.result;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في جلب ملفات الفصل");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في جلب ملفات الفصل");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

// Add new teacher file
export const addTeacherFile = createAsyncThunk(
  "teacherFiles/addTeacherFile",
  async ({ title, class_id, file }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();
      formData.append("title", title);
      formData.append("class_id", class_id);
      formData.append("file", file);

      const response = await axios.post(`${BASE_URL}/teacher_files/add_new`, formData, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return data.result;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في إضافة الملف");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في إضافة الملف");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

// Delete teacher file
export const deleteTeacherFile = createAsyncThunk(
  "teacherFiles/deleteTeacherFile",
  async (fileId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token3almny");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${BASE_URL}/teacher_files/delete_file/${fileId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      if (data.status === "success") {
        return fileId;
      } else {
        return rejectWithValue(data.message || "حدث خطأ في حذف الملف");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message || "حدث خطأ في حذف الملف");
      } else if (error.message && error.message.toLowerCase().includes("network")) {
        return rejectWithValue("حدث خطأ في الاتصال بالخادم");
      } else {
        return rejectWithValue(error.message || "حدث خطأ غير متوقع");
      }
    }
  }
);

const initialState = {
  files: [],
  loading: false,
  error: null,
  pagination: null,
  currentPage: 1,
  addFileLoading: false,
  addFileError: null,
  deleteFileLoading: false,
  deleteFileError: null,
};

const teacherFilesSlice = createSlice({
  name: "teacherFiles",
  initialState,
  reducers: {
    clearTeacherFilesError: (state) => {
      state.error = null;
    },
    clearAddFileError: (state) => {
      state.addFileError = null;
    },
    clearDeleteFileError: (state) => {
      state.deleteFileError = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch teacher class files
      .addCase(fetchTeacherClassFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherClassFiles.fulfilled, (state, action) => {
        state.loading = false;
        state.files = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchTeacherClassFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add teacher file
      .addCase(addTeacherFile.pending, (state) => {
        state.addFileLoading = true;
        state.addFileError = null;
      })
      .addCase(addTeacherFile.fulfilled, (state, action) => {
        state.addFileLoading = false;
        // Add the new file to the list
        if (action.payload) {
          state.files.unshift(action.payload);
        }
      })
      .addCase(addTeacherFile.rejected, (state, action) => {
        state.addFileLoading = false;
        state.addFileError = action.payload;
      })
      // Delete teacher file
      .addCase(deleteTeacherFile.pending, (state) => {
        state.deleteFileLoading = true;
        state.deleteFileError = null;
      })
      .addCase(deleteTeacherFile.fulfilled, (state, action) => {
        state.deleteFileLoading = false;
        // Remove the deleted file from the list
        state.files = state.files.filter(file => file.id !== action.payload);
      })
      .addCase(deleteTeacherFile.rejected, (state, action) => {
        state.deleteFileLoading = false;
        state.deleteFileError = action.payload;
      });
  },
});

export const { 
  clearTeacherFilesError, 
  clearAddFileError, 
  clearDeleteFileError, 
  setCurrentPage 
} = teacherFilesSlice.actions;

export default teacherFilesSlice.reducer;
